import { batch1 } from '@/pages/article/pillarData';
import { batch2 } from '@/pages/article/pillarData2';

export const SITE_NAME = 'AllChinaBuy Spreadsheet';

export const SITE_URL = ((import.meta.env.VITE_SITE_URL as string) || '').replace(/\/+$/, '');

export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

export interface SeoMeta {
  path: string;
  title: string;
  description: string;
  keywords: string;
  image?: string;
  published?: string;
}

function excerpt(text: string, max = 155): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max).replace(/\s+\S*$/, '') + '…';
}

const pillarKeywords: Record<string, string> = {
  '/allchinabuy-review/': 'AllChinaBuy review, AllChinaBuy review 2026, AllChinaBuy buying experience',
  '/allchinabuy-shipping-cost/': 'AllChinaBuy shipping cost, AllChinaBuy shipping rates, AllChinaBuy shipping guide',
  '/allchinabuy-warehouse/': 'AllChinaBuy warehouse, AllChinaBuy storage, AllChinaBuy consolidation',
  '/allchinabuy-coupon/': 'AllChinaBuy coupon, AllChinaBuy discount, AllChinaBuy promo',
  '/is-allchinabuy-legit/': 'is AllChinaBuy legit, AllChinaBuy review, AllChinaBuy safe',
  '/allchinabuy-seller-guide/': 'AllChinaBuy sellers, AllChinaBuy seller guide, reliable sellers',
  '/how-to-find-products/': 'AllChinaBuy product search, find AllChinaBuy products, AllChinaBuy spreadsheet',
  '/allchinabuy-buying-guide/': 'AllChinaBuy buying guide, AllChinaBuy order guide, AllChinaBuy shopping',
  '/allchinabuy-shipping-calculator/': 'AllChinaBuy shipping calculator, AllChinaBuy shipping estimate',
  '/common-allchinabuy-mistakes/': 'AllChinaBuy mistakes, AllChinaBuy tips, AllChinaBuy beginners',
};

const pillarSeo: SeoMeta[] = [...batch1, ...batch2].map((a) => ({
  path: a.slug,
  title: a.title,
  description: excerpt(a.intro),
  keywords: pillarKeywords[a.slug] || 'AllChinaBuy, AllChinaBuy spreadsheet, AllChinaBuy guide',
  image: a.image,
  published: '2026-08-01',
}));

export const routeSeoMap: Record<string, SeoMeta> = {
  '/': {
    path: '/',
    title: 'AllChinaBuy Spreadsheet – Products, Sellers, QC & Buying Guides',
    description:
      'Explore the latest AllChinaBuy spreadsheet, product resources, seller guides, QC tips, shipping information and buying guides. Find products faster and shop smarter.',
    keywords: 'AllChinaBuy spreadsheet, AllChinaBuy review, AllChinaBuy shipping, AllChinaBuy guide, buying guide',
    image: FAVICON_URL,
  },
  '/allchinabuy-spreadsheet/': {
    path: '/allchinabuy-spreadsheet/',
    title: 'AllChinaBuy Spreadsheet Complete Guide 2026',
    description:
      'The complete AllChinaBuy spreadsheet guide: how to find products, evaluate sellers, read QC photos, compare shipping methods and shop smarter with an organized buying resource.',
    keywords: 'AllChinaBuy spreadsheet, AllChinaBuy spreadsheet guide, AllChinaBuy buying guide, product spreadsheet',
    image: FAVICON_URL,
    published: '2026-08-01',
  },
  '/how-to-read-qc-photos/': {
    path: '/how-to-read-qc-photos/',
    title: 'How to Read AllChinaBuy QC Photos: A Complete Guide for Buyers',
    description:
      'Learn how to read AllChinaBuy QC photos like a pro. Spot defects, verify logos and stitching, check measurements, and approve shipments with confidence.',
    keywords: 'AllChinaBuy QC photos, AllChinaBuy quality control, AllChinaBuy inspection guide',
    image: FAVICON_URL,
    published: '2026-08-01',
  },
  '/shipping-methods-compared/': {
    path: '/shipping-methods-compared/',
    title: 'AllChinaBuy Shipping Methods Compared: Costs, Speed & Tracking Guide',
    description:
      'Compare AllChinaBuy shipping methods side by side: EMS, DHL, UPS, sea freight and postal options. Understand costs, speed, tracking and how to save money.',
    keywords: 'AllChinaBuy shipping, AllChinaBuy shipping methods, AllChinaBuy shipping cost',
    image: FAVICON_URL,
    published: '2026-08-01',
  },
  '/is-allchinabuy-safe/': {
    path: '/is-allchinabuy-safe/',
    title: 'Is AllChinaBuy Safe? A Complete Security Guide for Buyers',
    description:
      'Is AllChinaBuy safe? Learn about account security, payment protection, seller verification and scam prevention to shop with confidence.',
    keywords: 'is AllChinaBuy safe, AllChinaBuy legit, AllChinaBuy scam prevention, AllChinaBuy security',
    image: FAVICON_URL,
    published: '2026-08-01',
  },
  ...Object.fromEntries(pillarSeo.map((s) => [s.path, s])),
};

export const DEFAULT_META: SeoMeta = routeSeoMap['/'];

export function getSeoForPath(pathname: string): SeoMeta {
  if (pathname !== '/' && !pathname.endsWith('/')) {
    return routeSeoMap[pathname + '/'] || DEFAULT_META;
  }
  return routeSeoMap[pathname] || DEFAULT_META;
}